import React, { useEffect, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
import { MapPin, Sparkles } from 'lucide-react';
// Major Florida cities with coordinates for map markers
const MAJOR_CITIES = [{
  name: 'Miami',
  lat: 25.7617,
  lng: -80.1918,
  size: 'lg'
}, {
  name: 'Orlando',
  lat: 28.5383,
  lng: -81.3792,
  size: 'lg'
}, {
  name: 'Tampa',
  lat: 27.9506,
  lng: -82.4572,
  size: 'lg'
}, {
  name: 'Jacksonville',
  lat: 30.3322,
  lng: -81.6557,
  size: 'lg'
}, {
  name: 'Tallahassee',
  lat: 30.4383,
  lng: -84.2807,
  size: 'md'
}, {
  name: 'Pensacola',
  lat: 30.4213,
  lng: -87.2169,
  size: 'md'
}, {
  name: 'Fort Lauderdale',
  lat: 26.1224,
  lng: -80.1373,
  size: 'md'
}, {
  name: 'Naples',
  lat: 26.142,
  lng: -81.7948,
  size: 'md'
}, {
  name: 'Key West',
  lat: 24.5551,
  lng: -81.78,
  size: 'sm'
}, {
  name: 'Daytona Beach',
  lat: 29.2108,
  lng: -81.0228,
  size: 'sm'
}, {
  name: 'Sarasota',
  lat: 27.3364,
  lng: -82.5307,
  size: 'sm'
}, {
  name: 'Gainesville',
  lat: 29.6516,
  lng: -82.3248,
  size: 'sm'
}];
const ALL_CITIES = ['Miami', 'Orlando', 'Tampa', 'Jacksonville', 'Fort Lauderdale', 'Sarasota', 'Naples', 'West Palm Beach', 'Boca Raton', 'St. Petersburg', 'Delray Beach', 'Jupiter', 'Palm Bay', 'Melbourne', 'Doral', 'Weston', 'Winter Haven', 'Altamonte Springs', 'New Smyrna Beach', 'Largo', 'Clearwater', 'Tallahassee', 'Pensacola', 'Gainesville', 'Lakeland', 'Fort Myers', 'Port St. Lucie', 'Palm Coast', 'Daytona Beach', 'Bradenton', 'Kissimmee', 'Venice', 'Hollywood', 'Bonita Springs', 'Coral Springs', 'Winter Park', 'Deerfield Beach', 'Destin', 'Panama City', 'Riverview', 'Pompano Beach', 'Homestead', 'Cape Coral', 'North Port', 'Lehigh Acres', 'Miramar', 'Aventura', 'Hialeah', 'Davie', 'Margate', 'Pembroke Pines', 'Coconut Creek', 'Lake Worth', 'Wellington', 'Ocala', 'St. Augustine', 'Apopka', 'Vero Beach', 'Sanford', 'Key West', 'Wesley Chapel'];
export function CoverageMap() {
  const {
    lang
  } = useLanguage();
  const text = t[lang].coverage;
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<any>(null);
  useEffect(() => {
    let mounted = true;
    async function initMap() {
      // Dynamic import of leaflet (avoids SSR issues and only loads when needed)
      const L = (await import('leaflet')).default;
      // Inject leaflet CSS
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      if (!mounted || !mapRef.current || leafletMapRef.current) return;
      // Initialize map centered on Florida
      const map = L.map(mapRef.current, {
        center: [27.8, -83.3],
        zoom: 6,
        zoomControl: false,
        scrollWheelZoom: false,
        dragging: true,
        attributionControl: false
      });
      // Add modern dark tile layer (CartoDB Dark Matter)
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png', {
        maxZoom: 12,
        minZoom: 5
      }).addTo(map);
      // Add labels layer on top
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png', {
        maxZoom: 12,
        minZoom: 5
      }).addTo(map);
      // Custom red pulse marker icon
      const createIcon = (size: 'lg' | 'md' | 'sm') => {
        const dim = size === 'lg' ? 24 : size === 'md' ? 18 : 14;
        return L.divIcon({
          className: 'mf-marker',
          html: `
            <div style="position:relative;width:${dim}px;height:${dim}px;">
              <div style="position:absolute;inset:0;background:#D32F2F;border-radius:50%;border:3px solid white;box-shadow:0 0 0 2px rgba(211,47,47,0.5),0 4px 12px rgba(0,0,0,0.4);z-index:2;"></div>
              <div style="position:absolute;inset:0;background:#D32F2F;border-radius:50%;animation:mf-pulse 2s infinite;opacity:0.6;"></div>
            </div>
          `,
          iconSize: [dim, dim],
          iconAnchor: [dim / 2, dim / 2]
        });
      };
      // Inject pulse animation keyframes
      if (!document.getElementById('mf-marker-styles')) {
        const style = document.createElement('style');
        style.id = 'mf-marker-styles';
        style.textContent = `
          @keyframes mf-pulse {
            0% { transform: scale(1); opacity: 0.6; }
            50% { transform: scale(2.2); opacity: 0; }
            100% { transform: scale(1); opacity: 0; }
          }
          .leaflet-container { background: #0a0a0a !important; font-family: inherit; }
          .leaflet-popup-content-wrapper { 
            background: #212121; 
            color: white; 
            border-radius: 12px;
            border: 1px solid rgba(211,47,47,0.5);
          }
          .leaflet-popup-tip { background: #212121; }
          .leaflet-popup-content { 
            margin: 12px 16px; 
            font-weight: 600; 
            font-size: 14px;
          }
          .leaflet-popup-close-button { color: white !important; }
        `;
        document.head.appendChild(style);
      }
      // Add markers
      MAJOR_CITIES.forEach(city => {
        const marker = L.marker([city.lat, city.lng], {
          icon: createIcon(city.size as any)
        }).addTo(map);
        marker.bindPopup(`<strong style="color:#FFC107;">${city.name}</strong><br/><span style="color:#9CA3AF;font-size:12px;">Active Coverage</span>`);
      });
      leafletMapRef.current = map;
    }
    initMap();
    return () => {
      mounted = false;
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, []);
  return <section className="py-24 bg-mf-black text-white relative overflow-hidden" data-id="element-97">
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-98">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-mf-red/10 blur-[120px] rounded-full" data-id="element-99"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-mf-yellow/5 blur-[120px] rounded-full" data-id="element-100"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-101">
        <div className="text-center mb-16" data-id="element-102">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 15
        }} className="text-4xl md:text-6xl font-condensed font-black uppercase tracking-tight mb-6" data-id="element-103">
            {text.title}
          </motion.h2>
          <motion.div initial={{
          width: 0
        }} whileInView={{
          width: 96
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} className="h-1.5 bg-mf-red mx-auto rounded-full mb-6" data-id="element-104"></motion.div>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-105">
            {text.subtitle}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch" data-id="element-106">
          {/* Modern Leaflet Map */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 20
        }} className="lg:col-span-7 relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl" style={{
          minHeight: '500px'
        }} data-id="element-107">
            <div ref={mapRef} className="w-full h-full absolute inset-0 z-0" data-id="element-108"></div>

            {/* Floating Stat Card */}
            <div className="absolute top-4 left-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 z-[400] shadow-xl" data-id="element-109">
              <div className="flex items-center gap-3" data-id="element-110">
                <div className="w-10 h-10 bg-mf-red rounded-xl flex items-center justify-center" data-id="element-111">
                  <Sparkles size={20} className="text-white" data-id="element-112" />
                </div>
                <div data-id="element-113">
                  <div className="text-2xl font-condensed font-black text-white" data-id="element-114">
                    60+
                  </div>
                  <div className="text-xs text-gray-300 uppercase tracking-wider" data-id="element-115">
                    Cities Covered
                  </div>
                </div>
              </div>
            </div>

            {/* Map Hint */}
            <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 z-[400]" data-id="element-116">
              <p className="text-xs text-gray-300 font-medium flex items-center" data-id="element-117">
                <MapPin size={12} className="mr-2 text-mf-red" data-id="element-118" />
                {text.mapHint}
              </p>
            </div>
          </motion.div>

          {/* Cities List */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="lg:col-span-5" data-id="element-119">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 h-full" data-id="element-120">
              <h3 className="text-xl font-condensed font-black text-white uppercase tracking-wider mb-6 flex items-center" data-id="element-121">
                <MapPin className="mr-2 text-mf-red" size={20} data-id="element-122" />
                Service Areas
              </h3>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 max-h-[420px] overflow-y-auto pr-2 scrollbar-thin" data-id="element-123">
                {ALL_CITIES.map((city, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.01
              }} className="flex items-center text-sm text-gray-300 font-medium hover:text-white hover:translate-x-1 transition-all cursor-default" data-id="element-124">
                    <div className="w-1.5 h-1.5 bg-mf-red rounded-full mr-2.5 flex-shrink-0" data-id="element-125"></div>
                    <span className="truncate" data-id="element-126">{city}</span>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}